import { IServerSideGetRowsParams, IServerSideGetRowsRequest, SetFilterValuesFuncParams } from 'ag-grid-enterprise'
import { AppThunkDispatch, store } from 'appState/store'
import {
  ObGridFilterByDateQuery,
  ObGridFilterByNumberQuery,
  ObGridFilterByTextQuery,
  obGridFilterQueryGenerator,
  obGridSortQueryGenerator,
  setFilterWidthDynamically,
} from 'components/ObGrid/ObGridUtils'
import { apiPaths, RelativeRootEnum } from 'configs/apiConfigs'
import { updateClaimsApiQuery, updateClaimsTable } from 'features/claims/claimsSlice'
import { OperatorTypeEnum } from 'types/tableTypes'
import { encodeUri, formatDecimals } from 'utils'
import {
  ClaimStatusOption,
  ensureClaimStatusLookups,
  getClaimStatusLookup,
  getDetailedClaimStatuses,
  getDetailedClaimStatusLookup,
  mapAppealStatusLabelsToCodes,
  mapClaimStatusLabelsToCodes,
  mapDetailedClaimStatusLabelsToCodes,
  resolveStatusDisplayName,
  resolveStatusOption,
} from 'features/claims/claimsApi'
import { httpClaims, httpTenantsService } from 'utils/http'

import { CSCreateQuery, CSRestQuery, Sort } from 'features/claims/claimTypes'
import { UserPreferencesKeysEnum } from 'features/settings/settingsTypes'
import { USER_LIMIT } from '../../ClaimDetails/Internal/Constants'
import { getAvailableUsername } from './BulkAssignAndUnAssign'
import { CLAIMS_SLOTS, CLAIMS_TABLE } from './ClaimsTableColumns'
import { SUPPORTED_OPERATION } from './ClaimsTableConstants'
import { setSupportedOperationsInLocalStorage } from './ClaimsTableHelper'
import { hydrateRowsFromEntities } from './rowHealper'

//this is the grid's callback that gets the rows from the server
export const createServerSideDataSource = (
  dispatch: AppThunkDispatch,
  batchId: string | undefined,
  viewArchived = false,
) => {
  return {
    getRows: async (params: IServerSideGetRowsParams) => {
      try {
        await ensureClaimStatusLookups()
      } catch (error) {
        console.error('Error loading claim status codes for filters:', error)
      }
      const builtQuery = buildQuery(params.request, batchId, viewArchived, (colId) => !!params.api.getColumn(colId))
      dispatch(
        updateClaimsTable({
          prop: 'itemsPerPage',
          value: params.request.startRow! + 1 + ' - ' + params.request.endRow!,
        }),
      )
      localStorage.removeItem('oaiClaimIds')
      const dataQuery = builtQuery ? `?${builtQuery}` : ''
      dispatch(updateClaimsApiQuery({ query: dataQuery }))

      fetchClaimsWithBuiltQuery(params, dataQuery, dispatch)
      return params
    },
  }
}

// Status columns display names but must be sorted by their enum code on the API
const SORT_PATH_BY_COLUMN: Record<string, string> = {
  claimStatusPayer: 'claimStatusCode',
  detailedClaimStatus: 'detailedClaimStatusCode',
}

export const buildQuery = (
  paramsRequest: IServerSideGetRowsRequest,
  batchId: string | undefined,
  viewArchived = false,
  isGridColumn?: (colId: string) => boolean,
) => {
  const query: any = []

  // Get filters from Redux to include filters from hidden columns
  const state = store.getState()
  const reduxFilters = state.claims?.commonClaimsFilters || {}

  // AG Grid reloads rows as soon as a column filter changes, before onFilterChanged syncs Redux. For grid columns
  // the AG Grid filterModel is the source of truth, otherwise a just-removed column filter is still sent from Redux.
  const reduxOnlyFilters = isGridColumn
    ? Object.fromEntries(Object.entries(reduxFilters).filter(([colId]) => !isGridColumn(colId)))
    : reduxFilters

  // Merge AG Grid filterModel with Redux filters (Redux takes precedence for missing keys)
  const mergedFilterModel = { ...reduxOnlyFilters, ...paramsRequest.filterModel }

  query.push(obGridFilterQueryGenerator('archived', '=', viewArchived ? 'true' : 'false')!)
  if (batchId) {
    query.push(obGridFilterQueryGenerator('batchCode', '=', batchId)!)
  }

  query.push(`Limit=100`)
  query.push(`Offset=` + paramsRequest.startRow)

  if (paramsRequest.sortModel.length > 0) {
    const colId = paramsRequest.sortModel[0].colId
    const path = SORT_PATH_BY_COLUMN[colId] ?? colId
    const sort = paramsRequest.sortModel[0].sort === 'desc' ? true : false
    const initialSortQuery = obGridSortQueryGenerator(path, sort)
    query.push(initialSortQuery)
  } else {
    const initialSortQuery = obGridSortQueryGenerator('created', true)
    query.push(initialSortQuery)
  }

  const mappedQuery = getMappedApiQueryBasedOnClaimsFilter(mergedFilterModel, query)

  return mappedQuery
}

async function fetchClaimsWithBuiltQuery(
  params: IServerSideGetRowsParams,
  dataQuery: string,
  dispatch: AppThunkDispatch,
) {
  try {
    params.api.showLoadingOverlay()

    const response = await httpClaims.get(apiPaths.claims(dataQuery))

    const data = response.data

    params.success({
      rowData: await entityMapping(data.data),
      rowCount: data.count,
    })

    const noDataAvailable: boolean = data.count === 0 && data.data.length <= 0

    const oaiClaimIds = data.data.map((claim: any) => claim.claimId)
    localStorage.setItem('oaiClaimIds', JSON.stringify(oaiClaimIds))

    if (noDataAvailable) {
      params.api.showNoRowsOverlay()
    } else {
      params.api.hideOverlay()
    }

    dispatch(
      updateClaimsTable({
        prop: 'totalItems',
        value: data.count,
      }),
    )
  } catch (error) {
    params.api.hideOverlay()
    console.error(error)
    params.fail()
  }
}

/**
 * TODO: filterModel type 'any' to be changed later
 */
const getMappedApiQueryBasedOnClaimsFilter = (filterModel: any, query: Array<string>): string => {
  if (window.location.pathname.includes('/claims/batch/') && filterModel['batchCode']) {
    const batchId = location.pathname.split('/')[3]
    query.push(obGridFilterQueryGenerator('batchId', '=', batchId)!)
    delete filterModel['batchCode']
  }
  Object.keys(filterModel).map((key) => {
    if (key === 'levelOfSupport') {
      const levelOfSupport = getLevelOfSupportQuery(filterModel, key, query)
      return levelOfSupport
    }

    switch (filterModel[key].filterType) {
      case OperatorTypeEnum.date:
        query.push(ObGridFilterByDateQuery(filterModel, key, query))
        break
      case OperatorTypeEnum.set:
        if (key === 'claimStatusPayer') {
          const valueList = mapClaimStatusLabelsToCodes(filterModel[key].values)
          if (valueList.length) {
            query.push(
              `Filter=${encodeUri({
                path: 'claimStatusCode',
                operator: 'in',
                valueList,
              })}`,
            )
          }
        } else if (key === 'detailedClaimStatus') {
          const valueList = mapDetailedClaimStatusLabelsToCodes(filterModel[key].values)
          if (valueList.length) {
            query.push(
              `Filter=${encodeUri({
                path: 'detailedClaimStatusCode',
                operator: 'in',
                valueList,
              })}`,
            )
          }
        } else if (key === 'owner') {
          const newQuery = filterModel[key].values.includes('Blank')

          const ownerQuery = filterModel[key].values.map((item: any) => (item === 'Blank' ? null : item))
          if (newQuery) {
            ownerQuery.push('Unassigned')
          }
          query.push(
            `Filter=${encodeUri({
              path: key,
              operator: 'in',
              valueList: ownerQuery,
            })}`,
          )
        } else if (key === 'appealStatus') {
          const valueList = mapAppealStatusLabelsToCodes(filterModel[key].values)
          if (valueList.length) {
            query.push(
              `Filter=${encodeUri({
                path: key,
                operator: 'in',
                valueList,
              })}`,
            )
          }
        } else {
          query.push(obGridFilterQueryGenerator(key, 'in', filterModel[key].values)!)
        }
        break
      case OperatorTypeEnum.number:
        query.push(ObGridFilterByNumberQuery(filterModel, key, query))
        break
      case OperatorTypeEnum.text:
        query.push(ObGridFilterByTextQuery(filterModel, key, query))
        break
      default:
        query.push(
          `Filter=${encodeUri({
            path: key,
            operator: 'like',
            value: '%' + filterModel[key].filter + '%',
          })}`,
        )
        break
    }
  })
  return query.join('&')
}

const reportMissingStatusCode = (column: string, row: any, raw: unknown, matched: ClaimStatusOption | undefined) => {
  if (matched || raw == null || String(raw).trim() === '') return
  console.error(`${column}: no enum code found for value "${raw}" (claim ${row.claimId ?? row.id})`)
}

export const entityMapping = async (data: any[]): Promise<any[]> => {
  const hyderatedData = await hydrateRowsFromEntities(data, CLAIMS_TABLE, CLAIMS_SLOTS)

  try {
    const [claimStatuses, detailedStatuses] = await Promise.all([
      getClaimStatusLookup(),
      getDetailedClaimStatusLookup(),
    ])

    return hyderatedData.map((row) => {
      const claimStatus = resolveStatusOption(claimStatuses, row.claimStatusPayer, row.claimStatusCode)
      const detailedStatus = resolveStatusOption(detailedStatuses, row.detailedClaimStatus, row.detailedClaimStatusCode)
      reportMissingStatusCode('Extracted Claim Status', row, row.claimStatusPayer, claimStatus)
      reportMissingStatusCode('Extracted Status Details', row, row.detailedClaimStatus, detailedStatus)

      return {
        ...row,
        claimStatusPayer: resolveStatusDisplayName(claimStatuses, row.claimStatusPayer, row.claimStatusCode),
        claimStatusCode: claimStatus?.code ?? null,
        detailedClaimStatus: resolveStatusDisplayName(
          detailedStatuses,
          row.detailedClaimStatus,
          row.detailedClaimStatusCode,
        ),
        detailedClaimStatusCode: detailedStatus?.code ?? null,
      }
    })
  } catch (error) {
    console.error('Error mapping claim status display names:', error)
    return hyderatedData
  }
}

export const onFilterOpened = (params: any, column: any) => {
  const filterInstance = params.api.getFilterInstance(column.column.colId)
  if (filterInstance && typeof filterInstance.setMiniFilter === 'function') {
    filterInstance.setMiniFilter('')
  }
}

export const userNameFilterParams = async () => {
  const oaiUserQuery = new CSRestQuery(USER_LIMIT, 0)
  const preferredTenant = sessionStorage.getItem(UserPreferencesKeysEnum.outboundAiPreferredTenant)
  //oaiUserQuery.addFilter(new Filter('_permissionId', null, IUserRoleGuidEnum.claimAccessPermission!))

  oaiUserQuery.addSort(new Sort('givenName'))
  oaiUserQuery.addSort(new Sort('familyName'))

  const manageQuery = new CSCreateQuery(oaiUserQuery)
  const prepareUrl = `tenants/${preferredTenant}/${RelativeRootEnum.users}`
  const url = apiPaths.userSearch(prepareUrl, manageQuery).replace(',', '')
  const updatedUrl = url.replace(',', '')
  const value = await httpTenantsService.get(updatedUrl)
  const listValues = value.data.data.map((item: any) => {
    return getAvailableUsername(item)
  })
  listValues.push('Blank')
  setFilterWidthDynamically(listValues)
  return listValues
}

export const filterParams = {
  defaultToNothingSelected: true,
  values: async (params: SetFilterValuesFuncParams) => {
    const filedName = params.colDef.field!
    if (filedName === 'levelOfSupport') {
      setSupportedOperationsInLocalStorage()
    }
    if (filedName === 'owner') {
      const listValues = await userNameFilterParams()
      params.success(listValues)
    } else {
      const query: string[] = []
      query.push(`limit=1000`)
      /*const currentTab = getCurrentSelectedTab()

      if (currentTab === ClaimTypesEnum.needsWork) {
        query.push(obGridFilterQueryGenerator('archived', '=', 'false')!)
      }
      if (currentTab === ClaimTypesEnum.onLiveCall) {
        query.push(obGridFilterQueryGenerator('archived', '=', 'false')!)
        query.push(obGridFilterQueryGenerator('calljobStatus', '=', '2')!)
      }
      if (currentTab === ClaimTypesEnum.queued) {
        query.push(obGridFilterQueryGenerator('archived', '=', 'false')!)
        query.push(obGridFilterQueryGenerator('calljobStatus', '=', '1')!)
      }
      if (currentTab === ClaimTypesEnum.archived) {
        query.push(obGridFilterQueryGenerator('archived', '=', 'true')!)
      }*/

      //TODO: 'Rest of the condition yet to be done'

      const dataQuery = query.length ? `?${query.join('&')}` : ''

      let listValues: string[] = []
      try {
        listValues =
          filedName === 'detailedClaimStatus'
            ? await getDetailedClaimStatuses()
            : (await httpClaims.get(apiPaths.entityUniqueValues(params.colDef.field!, dataQuery))).data.data.map(
                (item: any) => item.group,
              )
      } catch (error) {
        console.error('Error fetching set filter values:', error)
        listValues = []
      }

      if (filedName !== 'levelOfSupport') {
        setFilterWidthDynamically(listValues)
      }

      if (filedName === 'levelOfSupport') {
        listValues.shift()
      } else {
        listValues.unshift(null as any)
      }
      params.success(listValues)
    }
  },
  buttons: ['apply', 'cancel'],
  closeOnApply: true,
}

function getLevelOfSupportQuery(filterModel: any, key: string, query: Array<string>) {
  const levelOfSupportFilter: string[] = []
  const values = filterModel[key].values
  const supportedOperations = JSON.parse(localStorage.getItem(SUPPORTED_OPERATION)!)

  values.forEach((value: any) => {
    supportedOperations?.[value === null ? 'NotSupported' : value].forEach((operation: any) => {
      if (operation.levelOfSupport === 'NotSupported') {
        levelOfSupportFilter.push(null as any, 'NotSupported')
      } else {
        levelOfSupportFilter.push(operation.levelOfSupport)
      }
    })
  })

  query.push(
    `Filter=${encodeUri({
      path: key,
      operator: 'in',
      valueList: key === 'levelOfSupport' ? [...new Set(levelOfSupportFilter)] : values,
    })}`,
  )

  return query.join('&')
}

export const currencyFormatter = (params: any) => {
  return params.value ? '$' + formatDecimals(params.value) : '---'
}
